import React, { useEffect, useMemo, useState } from "react";
import { FaChevronRight } from "react-icons/fa";
import { ordersApi } from "../services/orders";
import type { Insights } from "../types/orders";
import { formatSingleAmount, formatTxnAmount } from "../utils/currency";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import "./EarningsInsights.css";
import { SHOW_TIERS } from "../lib/tiersPaused";
import { t as tr, formatLocale } from "../i18n";
import { T } from "../i18n/T";

// "Total earnings" row + the Insights drawer behind its "View insights ›"
// link. Lived on the Balance tab originally (founder: earnings belong
// with the DJ's profile); extracted verbatim so the Profile tab can host it.
// Earnings figures arrive as props from the page's existing /users/:slug
// fetch — this component only fetches insights, and only when the drawer
// actually opens (the endpoint aggregates the DJ's full history).

// One-sentence explainers behind the (i) buttons — only for rows whose
// meaning isn't obvious from the label.
const INSIGHT_INFO: Record<string, { title: string; body: string }> = {
  avgPrice: {
    title: tr("earningsInsights.averagePrice2"),
    body: tr("earningsInsights.whatTheAveragePlayed"),
  },
  avgResponse: {
    title: tr("earningsInsights.avgResponseTime2"),
    body: tr("earningsInsights.howLongYouTake"),
  },
  bestNight: {
    title: tr("earningsInsights.bestNightEver2"),
    body: tr("earningsInsights.yourBiggestSingleNight"),
  },
  topFans: {
    title: tr("earningsInsights.topFans2"),
    body: tr("earningsInsights.yourMostFrequentRequesters"),
  },
};

function fmtResponse(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtJoined(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(formatLocale(), { month: "short", day: "numeric", year: "numeric" });
}

// Best-night dates arrive as plain "YYYY-MM-DD". Parse the parts manually —
// new Date("YYYY-MM-DD") is treated as UTC midnight and shows the PREVIOUS
// day in negative-offset timezones.
function fmtNight(day?: string | null): string | null {
  if (!day) return null;
  const [y, m, d] = String(day).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString(formatLocale(), { month: "short", day: "numeric", year: "numeric" });
}

const MEDALS = ["🥇", "🥈", "🥉"];

type EarnedByCurrency = Record<string, { gross: number; net: number; count: number }>;

// One label for lifetime earnings: every currency with a gross > 0, joined
// with " • "; falls back to the DJ's own currency when nothing has landed yet.
// Exported so the Lifetime Earnings row inside the Edit-profile drawer prints the
// exact same string as the Insights hero.
export function formatTotalEarnings(
  earnedByCurrency: EarnedByCurrency,
  earnedAmount: number,
  ownCurrency: string,
): string {
  const parts = Object.entries(earnedByCurrency)
    .filter(([, v]) => (v?.gross || 0) > 0)
    // formatTxnAmount, not formatSingleAmount: thousands separators — a lifetime
    // figure is the one number here that routinely passes 1,000 (build 38).
    .map(([cur, v]) => formatTxnAmount(v?.gross || 0, cur));
  return parts.length > 0 ? parts.join(" • ") : formatTxnAmount(earnedAmount, ownCurrency);
}

// The "Lifetime Earnings" menu row on its own (founder 2026-09-15: not "My
// Earnings", so it is never confused with the Balance tab) (icon · label · amount · chevron).
// Rendered by EarningsInsights below when it owns the row, and by the Profile
// page inside the Edit-profile drawer (founder 2026-09-15), where the drawer
// closes and signals the Insights sheet open instead of nesting a sheet in a
// sheet. Same classes as its sibling rows so it restyles with the list.
/** Balance-tab dollar icon, fill reversed — shared by the row and the Edit-profile card. */
export const EARNINGS_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/><path fill="currentColor" d="M13.41 18.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.59-4.18-1.61-4.18-3.66 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/></svg>
);

export const EarningsRow: React.FC<{ amount: string; onClick: () => void }> = ({ amount, onClick }) => (
  <button type="button" className="profile-menu-row" onClick={onClick}>
    {/* Balance-tab dollar icon with the fill reversed (founder decision):
        outlined ring + solid $ glyph — same drawing, inverted treatment. */}
    <span className="profile-menu-ic" aria-hidden="true">
      {EARNINGS_ICON}
    </span>
    <span className="profile-menu-label">{tr("earningsInsights.lifetimeEarnings")}</span>
    <span className="profile-menu-val profile-menu-val--amount">{amount}</span>
    <FaChevronRight className="profile-menu-chev" aria-hidden />
  </button>
);

interface Props {
  earnedByCurrency: EarnedByCurrency;
  // Loyalty-ladder teaser under Lifetime earnings (founder decision):
  // "$484.03 to go → Silver". null/absent = no line (tiers off, custom
  // rate, or already Legend).
  tierNext?: { remaining: number; name: string } | null;
  earnedAmount: number;
  ownCurrency: string;
  // false = render only the Insights sheet; the row lives elsewhere (the
  // Edit-profile drawer on the Profile page). Default true.
  showRow?: boolean;
  // Bump this number to open the Insights sheet from outside (the drawer row).
  openSignal?: number;
}

const EarningsInsights: React.FC<Props> = ({ earnedByCurrency, earnedAmount, ownCurrency, tierNext, showRow = true, openSignal = 0 }) => {
  const [insights, setInsights] = useState<Insights | null>(null);
  const [insightsFailed, setInsightsFailed] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [infoKey, setInfoKey] = useState<string | null>(null);

  // Insights — fetched only when the drawer actually opens. One fetch per
  // visit: `insights` caches reopens. tzOffset lets the server cut "nights"
  // at 6am in the DJ's own timezone.
  useEffect(() => {
    if (!insightsOpen || insights || insightsFailed) return;
    let cancelled = false;
    ordersApi.insights(new Date().getTimezoneOffset())
      .then((res) => { if (!cancelled) setInsights(res || null); })
      .catch(() => { if (!cancelled) setInsightsFailed(true); });
    return () => { cancelled = true; };
  }, [insightsOpen, insights, insightsFailed]);

  const totalEarningsDisplay = useMemo(
    () => formatTotalEarnings(earnedByCurrency, earnedAmount, ownCurrency),
    [earnedByCurrency, ownCurrency, earnedAmount],
  );

  // Opened from outside (the Lifetime Earnings row inside the Edit-profile drawer):
  // any new non-zero signal opens the sheet.
  useEffect(() => {
    if (openSignal) setInsightsOpen(true);
  }, [openSignal]);

  const avgResponse = fmtResponse(insights?.avgResponseMs);
  const dateJoined = fmtJoined(insights?.dateJoined);

  const acceptedPct = useMemo(() => {
    if (!insights) return null;
    const a = insights.acceptedCount || 0;
    const d = insights.declinedCount || 0;
    const decided = a + d;
    // null (row hides) rather than a scary-looking "Accepted 0%" for a
    // talent who simply hasn't received any requests yet.
    return decided > 0 ? Math.round((a / decided) * 100) : null;
  }, [insights]);

  const totalRequests = useMemo(() => {
    if (!insights) return null;
    return (insights.acceptedCount || 0) + (insights.declinedCount || 0);
  }, [insights]);

  const avgPriceDisplay = useMemo(() => {
    const played = insights?.playedByCurrency || {};
    const parts = Object.entries(played)
      .filter(([, v]) => (v?.count || 0) > 0)
      .map(([cur, v]) => formatSingleAmount(v.gross / v.count, cur));
    return parts.length > 0 ? parts.join(" • ") : null;
  }, [insights]);

  const tipsDisplay = useMemo(() => {
    const tips = insights?.tipsByCurrency || {};
    const parts = Object.entries(tips)
      .filter(([, amt]) => (amt || 0) > 0)
      .map(([cur, amt]) => formatSingleAmount(amt || 0, cur));
    return parts.length > 0 ? parts.join(" • ") : null;
  }, [insights]);

  const fanTotal = (t?: Record<string, number>) => {
    if (!t) return null;
    const parts = Object.entries(t)
      .filter(([, amt]) => (amt || 0) > 0)
      .map(([cur, amt]) => formatSingleAmount(amt || 0, cur, 0));
    return parts.length > 0 ? parts.join(" • ") : null;
  };

  const bestNightDisplay = useMemo(() => {
    const bn = insights?.bestNight;
    if (!bn || !bn.date || !(typeof bn.amount === "number" && bn.amount > 0)) return null;
    const night = fmtNight(bn.date);
    if (!night) return null;
    return `${formatSingleAmount(bn.amount, bn.currency)} · ${night}`;
  }, [insights]);

  const infoBtn = (k: keyof typeof INSIGHT_INFO) => (
    <button
      type="button"
      className="insights-info-btn"
      aria-label={`About ${INSIGHT_INFO[k].title}`}
      onClick={() => setInfoKey(k)}
    >
      i
    </button>
  );

  return (
    <>
      {showRow && (
        <EarningsRow amount={totalEarningsDisplay} onClick={() => setInsightsOpen(true)} />
      )}

      <BottomSheet
        open={insightsOpen}
        title={tr("profilePage.requestStats")}
        icon={PROFILE_ICONS.stats}
        onClose={() => setInsightsOpen(false)}
      >
        <div className="insights-hero">
          <div className="insights-hero-amount">{totalEarningsDisplay}</div>
          <div className="insights-hero-sub">{tr("earningsInsights.lifetimeEarnings2")}</div>
          {/* Hidden while Tier Level is paused (lib/tiersPaused). */}
          {SHOW_TIERS && tierNext && (
            <div className="insights-tier-line">
              <T k="earningsInsights.toGo" vars={{ amount: formatSingleAmount(tierNext.remaining, ownCurrency), name: tierNext.name }} tags={{ 0: (c) => <b>{c}</b> }} />
            </div>
          )}
        </div>

        {totalRequests != null && (
          <div className="insights-row"><span>{tr("earningsInsights.totalRequests")}</span><b>{totalRequests}</b></div>
        )}
        {acceptedPct != null && (
          <div className="insights-row"><span>{tr("earningsInsights.accepted")}</span><b className="grn">{acceptedPct}%</b></div>
        )}
        {avgPriceDisplay && (
          <div className="insights-row"><span>{tr("earningsInsights.averagePrice")}{infoBtn("avgPrice")}</span><b>{avgPriceDisplay}</b></div>
        )}
        {avgResponse && (
          <div className="insights-row"><span>{tr("earningsInsights.avgResponseTime")}{infoBtn("avgResponse")}</span><b>{avgResponse}</b></div>
        )}
        {tipsDisplay && (
          <div className="insights-row"><span>{tr("earningsInsights.tipsEarned")}</span><b className="grn">{tipsDisplay}</b></div>
        )}
        {(insights?.playlistsSold || 0) > 0 && (
          <div className="insights-row"><span>{tr("earningsInsights.playlistsSold")}</span><b className="grn">{insights?.playlistsSold}</b></div>
        )}
        {bestNightDisplay && (
          <div className="insights-row"><span>{tr("earningsInsights.bestNightEver")}{infoBtn("bestNight")}</span><b className="grn">{bestNightDisplay}</b></div>
        )}
        {insights?.topArtists && insights.topArtists.length > 0 && (
          <div className="insights-row"><span>{tr("earningsInsights.topArtistFansRequest")}</span><b>{insights.topArtists[0].name}</b></div>
        )}
        {dateJoined && (
          <div className="insights-row"><span>{tr("earningsInsights.dateJoined")}</span><b>{dateJoined}</b></div>
        )}

        {insights?.topFans && insights.topFans.length > 0 && (
          <>
            <div className="insights-section">{tr("earningsInsights.topFans")}{infoBtn("topFans")}</div>
            {insights.topFans.slice(0, 3).map((fan, i) => (
              <div className="insights-row" key={`${fan.name}-${i}`}>
                <span>{MEDALS[i] || ""} {fan.name}</span>
                <span className="insights-fan-meta">
                  {tr("earningsInsights.requestsCount", { count: fan.count })}
                  {fanTotal(fan.totalByCurrency) && (
                    <> · <b className="grn">{fanTotal(fan.totalByCurrency)}</b></>
                  )}
                </span>
              </div>
            ))}
          </>
        )}

        {!insights && (
          <div className="insights-empty">
            {insightsFailed ? tr("earningsInsights.insightsArenTAvailable") : tr("earningsInsights.insightsAreLoading")}
          </div>
        )}
      </BottomSheet>

      {/* (i) explainer — separate fixed overlay so it stacks ABOVE the
          drawer (BottomSheet overlay is z-index 1000; tooltips nested in
          .bsheet-body would be clipped by its overflow scroll). Tap
          anywhere to dismiss. */}
      {infoKey && INSIGHT_INFO[infoKey] && (
        <div
          className="insights-info-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setInfoKey(null)}
        >
          <div className="insights-info-card">
            <div className="insights-info-title">{INSIGHT_INFO[infoKey].title}</div>
            <div className="insights-info-body">{INSIGHT_INFO[infoKey].body}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default EarningsInsights;
